import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from 'react-loader-spinner';

export type ImageDownloadOption = 'cover' | 'gallery';

interface IDownloadImagesModal {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onSubmit: (allImages: boolean) => void;
}

const DownloadImagesModal: FC<IDownloadImagesModal> = ({ open, loading, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [option, setOption] = useState<ImageDownloadOption>('cover');

  useEffect(() => {
    if (open) setOption('cover');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [loading, onClose, open]);

  if (!open) return null;

  return (
    <div
      className="cto-downloadImagesModal"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !loading) onClose();
      }}
    >
      <div
        className="cto-downloadImagesModal__wrapper"
        role="dialog"
        aria-modal="true"
        aria-labelledby="downloadImagesModalTitle"
      >
        <div className="cto-downloadImagesModal__heading">
          <h3 className="cto-downloadImagesModal__title" id="downloadImagesModalTitle">
            {t('DownloadImages:DownloadProductImages')}
          </h3>
          <button
            type="button"
            className="cto-downloadImagesModal__close"
            onClick={onClose}
            disabled={loading}
            aria-label={t('DownloadImages:Close')}
          >
            ×
          </button>
        </div>
        <div className="cto-downloadImagesModal__content">
          <div className="cto-downloadImagesModal__options">
            <label className="cto-downloadImagesModal__option">
              <input
                type="radio"
                name="downloadImagesOption"
                value="cover"
                checked={option === 'cover'}
                onChange={() => setOption('cover')}
                disabled={loading}
              />
              <span>{t('DownloadImages:CoverOnly')}</span>
            </label>
            <label className="cto-downloadImagesModal__option">
              <input
                type="radio"
                name="downloadImagesOption"
                value="gallery"
                checked={option === 'gallery'}
                onChange={() => setOption('gallery')}
                disabled={loading}
              />
              <span>{t('DownloadImages:WholeGallery')}</span>
            </label>
          </div>
        </div>
        <div className="cto-downloadImagesModal__actions">
          <button type="button" className="cto-downloadImagesModal__cancel" onClick={onClose} disabled={loading}>
            {t('DownloadImages:Cancel')}
          </button>
          <button
            type="button"
            className="cto-downloadImagesModal__submit"
            onClick={() => onSubmit(option === 'gallery')}
            disabled={loading}
          >
            {loading ? (
              <Loader type="ThreeDots" color="#fff" width={40} height={10} />
            ) : (
              t('DownloadImages:DownloadButton')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadImagesModal;
